import React from 'react';
import { Dialog } from '@progress/kendo-react-dialogs';
import { Button } from '@progress/kendo-react-buttons';
import { Input } from './form/Input';
import styled from 'styled-components';
import { trashIcon, plusIcon } from '@progress/kendo-svg-icons';
import config from '../config/config';

const commonInputStyle = { width: '300px' };

const ContactPersonContainer = styled.div`
  margin-bottom: 20px;
  margin-left: 20px;
`;

const ContactPersonFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 300px;
`;

const InputWithButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const PlusButton = styled(Button)`
  margin-top: 10px;
  margin-left: 10px;
`;

const TrashButton = styled(Button)`
  margin-top: 10px;
  margin-left: 5px;
`;

const ContactPersonsModal = ({ visible, onClose, jobID, initialContactPersons, showNotification }) => {
  const [contactPersons, setContactPersons] = React.useState([]);
  const [originalContactPersons, setOriginalContactPersons] = React.useState([]);

  React.useEffect(() => {
    if (visible) {
      const initialData = initialContactPersons.length > 0 
        ? initialContactPersons 
        : [{ name: '', jobrole: '', email: '', phone: '' }];
      setContactPersons([...initialData]);
      setOriginalContactPersons([...initialData]);
    }
  }, [visible, initialContactPersons]);

  const addContactPerson = () => {
    setContactPersons(prev => [...prev, { name: '', jobrole: '', email: '', phone: '' }]);
  };

  const removeContactPerson = (index) => {
    setContactPersons(prev => prev.filter((_, i) => i !== index));
  };

  const updateContactPerson = (index, field, value) => {
    setContactPersons(prev => {
      const newContactPersons = [...prev];
      newContactPersons[index] = { ...newContactPersons[index], [field]: value };
      return newContactPersons;
    });
  };

  const saveContactPersons = async () => {
    try {
      const filteredContactPersons = contactPersons.filter(person =>
        person.name.trim() !== '' || person.jobrole.trim() !== '' || person.email.trim() !== '' || person.phone.trim() !== ''
      );
      await fetch(`${config.host}/jobs/${jobID}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Ansprechpartner: filteredContactPersons }),
      });
      showNotification('success', 'Ansprechpartner erfolgreich gespeichert!');
      onClose(filteredContactPersons);
    } catch (error) {
      console.error('Error saving contact persons:', error);
      showNotification('error', 'Fehler beim Speichern der Ansprechpartner.');
    }
  };

  const handleClose = () => {
    setContactPersons([...originalContactPersons]);
    onClose();
  };

  if (!visible) return null;

  return (
    <Dialog title="Ansprechpartner bearbeiten" onClose={handleClose} width={600}>
      <div style={{ padding: '20px' }}>
        {contactPersons.map((person, index) => (
          <ContactPersonContainer key={index}>
            <ContactPersonFields>
              <InputWithButtons>
                <Input
                  value={person.name}
                  onChange={(e) => updateContactPerson(index, 'name', e.target.value)}
                  label={<strong>{`AP ${index + 1}`}</strong>}
                  style={commonInputStyle}
                />
                <PlusButton svgIcon={plusIcon} onClick={addContactPerson} fillMode="flat" />
                {index > 0 && (
                  <TrashButton svgIcon={trashIcon} onClick={() => removeContactPerson(index)} fillMode="flat" />
                )}
              </InputWithButtons>
              <Input
                value={person.jobrole}
                onChange={(e) => updateContactPerson(index, 'jobrole', e.target.value)}
                label={<strong>{`Funktion AP ${index + 1}`}</strong>}
                style={commonInputStyle}
              />
              <Input
                value={person.email}
                onChange={(e) => updateContactPerson(index, 'email', e.target.value)}
                label={<strong>{`E-Mail AP ${index + 1}`}</strong>}
                style={commonInputStyle}
              />
              <Input
                value={person.phone}
                onChange={(e) => updateContactPerson(index, 'phone', e.target.value)}
                label={<strong>{`Telefon AP ${index + 1}`}</strong>}
                style={commonInputStyle}
              />
            </ContactPersonFields>
          </ContactPersonContainer>
        ))}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
          <Button onClick={handleClose}>Abbrechen</Button>
          <Button themeColor="primary" onClick={saveContactPersons}>Speichern</Button>
        </div>
      </div>
    </Dialog>
  );
};

export default ContactPersonsModal;
